import {
  EMAIL_REGEX,
  PASSWORD_REGEX,
} from '@bulknutrients/bulk-shared-lib/constants';
import {
  mergeStyles,
  validatePassword,
} from '@bulknutrients/bulk-shared-lib/utils';
import { Button } from '@components/Button';
import { Icons } from '@components/Icons/Icons';
import { ALLOWED_FILE_TYPES } from '@data/fileTypes';
import { ErrorMessage } from '@hookform/error-message';
import * as RadixSelect from '@radix-ui/react-select';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import Image from 'next/image';
import {
  type ReactElement,
  type ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Controller, useFormContext } from 'react-hook-form';
import { twJoin } from 'tailwind-merge';

// This returns React Hook Form validation options
export const formatValidationWithErrors = (props, name) => {
  const validators = {} as Record<string, unknown>;

  if (props.required) {
    validators.required = { value: true, message: `${name} is required` };
  }

  if (props.minLength) {
    validators.minLength = {
      value: Number.parseInt(props.minLength),
      message: `Minimum length for ${name} is ${props.minLength} characters`,
    };
  }

  if (props.maxLength) {
    validators.maxLength = {
      value: Number.parseInt(props.maxLength),
      message: `Maximum length for ${name} is ${props.maxLength} characters`,
    };
  }

  if (props.min !== null && typeof props.min !== 'undefined') {
    if (props.type === 'date') {
      const date = new Date();
      const daysToSubtract =
        typeof props.min === 'string' ? Number.parseInt(props.min) : props.min;
      date.setDate(date.getDate() + daysToSubtract);
      const minDate = `${date.getFullYear()}-${(
        `0${date.getMonth() + 1}`
      ).slice(-2)}-${(`0${date.getDate()}`).slice(-2)}`;
      validators.min = {
        value: minDate,
        message: `Minimum date for ${name} is ${minDate}`,
      };
    } else {
      validators.min = {
        value:
          typeof props.min === 'string'
            ? Number.parseInt(props.min)
            : props.min,
        message: `Minimum value for ${name} is ${props.min}`,
      };
    }
  }

  if (props.max !== null && typeof props.max !== 'undefined') {
    if (props.type === 'date') {
      const date = new Date();
      const daysToAdd =
        typeof props.max === 'string' ? Number.parseInt(props.max) : props.max;
      date.setDate(date.getDate() + daysToAdd);
      const maxDate = `${date.getFullYear()}-${(
        `0${date.getMonth() + 1}`
      ).slice(-2)}-${(`0${date.getDate()}`).slice(-2)}`;
      validators.max = {
        value: maxDate,
        message: `Maximum date for ${name} is ${maxDate}`,
      };
    } else {
      validators.max = {
        value:
          typeof props.max === 'string'
            ? Number.parseInt(props.max)
            : props.max,
        message: `Maximum value for ${name} is ${props.max}`,
      };
    }
  }

  if (props.pattern) {
    validators.pattern = {
      value: props.pattern,
      message: `${name} does not match the required format`,
    };
  }

  return validators;
};

function FieldWrapper({ children }: { children: ReactNode }): ReactElement {
  return <div className="flex flex-col gap-1 mb-4">{children}</div>;
}

export function Label({
  label,
  fieldName,
  className,
}: {
  label: string | ReactNode;
  fieldName: string;
  className?: string;
}): ReactElement {
  return typeof label === 'string' ? (
    <label
      htmlFor={fieldName}
      className={twJoin([className ?? '', 'text-xs pl-2 font-bold'])}
    >
      {label}
    </label>
  ) : (
    <>{label}</>
  );
}

function Description({ description }: { description: string }): ReactElement {
  return <span className="description pl-2">{description}</span>;
}

export function ErrorText({ message }: { message: string }): ReactElement {
  return (
    <span className="text-red before:inline before:content-['⚠_']">
      {message}
    </span>
  );
}

export function CheckboxRow({
  id,
  name,
  label,
  disabled = false,
  className = '',
  labelClassName = '',
  inputClassName = '',
  ...inputProps
}: {
  id: string;
  name?: string;
  label: ReactNode;
  disabled?: boolean;
  className?: string;
  labelClassName?: string;
  inputClassName?: string;
} & Record<string, any>): ReactElement {
  const resolvedName = name ?? inputProps.name;

  return (
    <label
      htmlFor={id}
      className={twJoin(
        'flex items-center gap-2.5',
        disabled ? 'cursor-not-allowed text-grey-40' : 'cursor-pointer',
        className
      )}
    >
      <input
        {...inputProps}
        id={id}
        name={resolvedName}
        type="checkbox"
        disabled={disabled}
        className="peer sr-only"
      />
      <span
        aria-hidden="true"
        className={twJoin(
          'flex items-center justify-center h-5 w-5 shrink-0 rounded-sm border border-black bg-white transition-colors',
          'peer-checked:bg-black peer-checked:border-black',
          'peer-checked:[&>svg]:opacity-100',
          'peer-focus-visible:outline-2 peer-focus-visible:outline-black peer-focus-visible:outline-offset-2',
          'peer-disabled:opacity-60',
          inputClassName
        )}
      >
        <Icons.CheckMark className="w-3 h-3 text-white opacity-0 transition-opacity" />
      </span>
      <span className={labelClassName}>{label}</span>
    </label>
  );
}

export function Input({
  name,
  label,
  inlineErrors = true,
  ...props
}: { name: string; label: string | ReactNode; inlineErrors?: boolean } & Record<
  string,
  any
>): ReactElement {
  if (props.type === 'radio') {
    return (
      <RadioInput
        name={name}
        label={label}
        inlineErrors={inlineErrors}
        {...props}
      />
    );
  }
  if (props.type === 'checkbox') {
    return (
      <CheckboxInput
        name={name}
        label={label}
        inlineErrors={inlineErrors}
        {...props}
      />
    );
  }
  if (props.type === 'date') {
    return (
      <DateInput
        name={name}
        label={label}
        inlineErrors={inlineErrors}
        {...props}
      />
    );
  }
  if (props.type === 'number') {
    return (
      <NumberInput
        name={name}
        label={label}
        inlineErrors={inlineErrors}
        {...props}
      />
    );
  }
  if (props.type === 'email') {
    return (
      <EmailInput
        name={name}
        label={label}
        inlineErrors={inlineErrors}
        {...props}
      />
    );
  }
  if (props.type === 'password') {
    return (
      <PasswordInput
        name={name}
        label={label}
        inlineErrors={inlineErrors}
        {...props}
      />
    );
  }
  if (props.type === 'search') {
    return (
      <SearchInput
        name={name}
        label={label}
        inlineErrors={inlineErrors}
        {...props}
      />
    );
  }
  return (
    <TextInput
      name={name}
      label={label}
      inlineErrors={inlineErrors}
      {...props}
    />
  );
}

export function Textarea({
  name,
  label,
  inlineErrors = true,
  ...props
}: { name: string; label: string; inlineErrors?: boolean } & Record<
  string,
  any
>): ReactElement {
  const { formState, register } = useFormContext();

  return (
    <FieldWrapper>
      <Label
        label={
          <div className="flex gap-2 items-center">
            <label
              htmlFor={name}
              className={mergeStyles(
                props.className ?? '',
                'text-xs pl-2 font-bold'
              )}
            >
              {label}
            </label>
            {inlineErrors && formState.errors[name] ? (
              <ErrorMessage
                errors={formState.errors}
                name={name}
                render={({ message }) => <ErrorText message={message} />}
              />
            ) : null}
          </div>
        }
        fieldName={name}
      />
      <textarea
        {...register(name, {
          ...formatValidationWithErrors(props, label),
          validate: props.validate ? props.validate : null,
        })}
        {...props}
        name={name}
        id={name}
        rows={props.rows ?? 3}
        className="resize-y border border-solid border-grey-mid bg-white text-black p-2.5 rounded-2xl"
      />
      {props.description && <Description description={props.description} />}
    </FieldWrapper>
  );
}

export function Select({
  name,
  label,
  bordered = true,
  className = '',
  inlineErrors = true,
  ...props
}: {
  name: string;
  label: string;
  className?: string;
  inlineErrors?: boolean;
  bordered?: boolean;
} & Record<string, any>): ReactElement {
  const { control } = useFormContext();
  // this open state is used for the carousel scale effect to work while options are open
  const [open, setOpen] = useState(false);

  if (!props.options || !Array.isArray(props.options)) {
    return <></>;
  }

  return (
    <div
      className={mergeStyles(className, 'relative w-full pb-2.5')}
      data-select-open={open || undefined}
    >
      <Controller
        name={name}
        control={control}
        rules={{
          ...formatValidationWithErrors(props, label),
          validate: props.validate ? props.validate : null,
        }}
        render={({ field }) => (
          <RadixSelect.Root
            value={field.value}
            onValueChange={field.onChange}
            open={open}
            onOpenChange={setOpen}
          >
            <RadixSelect.Trigger
              className={`font-inherit h-10 items-center cursor-pointer
                m-0 w-full max-w-[275px] text-left justify-between flex
                appearance-none rounded-full ${bordered ? 'border border-black' : ''} bg-grey-light px-3 gap-3
                leading-inherit text-black outline-none relative font-bold z-1`}
            >
              <RadixSelect.Value placeholder={label}>
                <span className="max-w-full line-clamp-1 text-ellipsis">
                  {props.options.find((o) => o.value == field.value)?.label}
                </span>
              </RadixSelect.Value>
              <RadixSelect.Icon asChild>
                <Icons.Chevron.Down className="h-4 w-4 transition-transform in-data-[state=open]:rotate-180" />
              </RadixSelect.Icon>
            </RadixSelect.Trigger>

            <RadixSelect.Portal>
              <RadixSelect.Content
                position="popper"
                sideOffset={4}
                className="border border-grey-light bg-white rounded-2xl z-3
                w-(--radix-select-trigger-width)
                max-h-(--radix-select-content-available-height)
                overflow-y-auto overflow-x-hidden"
              >
                <RadixSelect.Viewport>
                  {props.options
                    .filter((o) => !(o === null)) // this is for dodgy data (when variants removed, usually affects staging)
                    .map((o) => (
                      <RadixSelect.Item
                        disabled={o.disabled ?? false}
                        key={o.value}
                        value={o.value}
                        className="cursor-pointer border-none py-2 outline-none text-black px-6
                        data-disabled:cursor-default data-disabled:text-grey-40 data-disabled:pointer-events-none 
                        hover:bg-orange hover:text-white active:bg-orange active:text-white rounded-2xl"
                      >
                        <RadixSelect.ItemText>{o.label}</RadixSelect.ItemText>
                      </RadixSelect.Item>
                    ))}
                </RadixSelect.Viewport>
              </RadixSelect.Content>
            </RadixSelect.Portal>
          </RadixSelect.Root>
        )}
      />
    </div>
  );
}

export function MultiSelect({
  name,
  label,
  bordered = true,
  className = '',
  inlineErrors = true,
  ...props
}: {
  name: string;
  label: string;
  bordered?: boolean;
  className?: string;
  inlineErrors?: boolean;
} & Record<string, any>): ReactElement {
  const { formState, control } = useFormContext();

  if (!props.options || !Array.isArray(props.options)) {
    return <></>;
  }

  return (
    <div className={mergeStyles(className, 'relative w-max pb-2.5')}>
      <Controller
        name={name}
        control={control}
        rules={{
          ...formatValidationWithErrors(props, label),
          validate: props.validate ? props.validate : null,
        }}
        render={({ field }) => (
          <MultiSelectDropdown
            name={name}
            value={field.value}
            onChange={field.onChange}
            options={props.options}
            label={label}
            bordered={bordered}
          />
        )}
      />
    </div>
  );
}

function MultiSelectDropdown({
  name,
  value,
  onChange,
  options,
  label,
  bordered = true,
}: {
  name: string;
  value: unknown;
  onChange: (value: string[]) => void;
  options: Array<{
    value: string;
    label: string;
    disabled?: boolean;
  }>;
  label: string;
  bordered?: boolean;
}): ReactElement {
  const panelId = useId(); // used for accessability to link the button and the dropdown panel
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });

  const cleanedOptions = options.filter((option) => option !== null); // this is in case data is corrupt (usually staging sanity)
  const selectedValues = Array.isArray(value)
    ? value.map((item) => String(item))
    : [];

  const selectedLabels = cleanedOptions
    .filter((option) => selectedValues.includes(String(option.value)))
    .map((option) => option.label);

  const buttonText =
    selectedLabels.length > 0
      ? `${selectedLabels.slice(0, 2).join(', ')}${
          selectedLabels.length > 2 ? ` +${selectedLabels.length - 2}` : ''
        }`
      : label;

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const updatePosition = () => {
      const triggerRect = triggerRef.current?.getBoundingClientRect();
      if (!triggerRect) {
        return;
      }

      const panelTop = triggerRect.bottom + 4;

      setPosition({
        top: panelTop,
        left: triggerRect.left,
        width: triggerRect.width,
      });
    };

    const onOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node;

      if (triggerRef.current?.contains(target)) {
        return;
      }

      if (panelRef.current?.contains(target)) {
        return;
      }

      setIsOpen(false);
    };

    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('mousedown', onOutsideClick);
    window.addEventListener('keydown', onEscape);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('mousedown', onOutsideClick);
      window.removeEventListener('keydown', onEscape);
    };
  }, [isOpen]);

  const toggleOption = (optionValue: string) => {
    if (selectedValues.includes(optionValue)) {
      onChange(selectedValues.filter((item) => item !== optionValue));
      return;
    }

    onChange([...selectedValues, optionValue]);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={isOpen}
        aria-controls={panelId}
        className={`font-inherit h-10 items-center cursor-pointer m-0 w-full max-w-[275px] text-left 
        justify-between flex appearance-none rounded-full ${bordered ? 'border border-black' : ''} 
        bg-grey-light px-3 gap-3 font-bold
        leading-inherit text-black outline-none relative z-1`}
        onClick={() => setIsOpen((open) => !open)}
      >
        <span className="max-w-full line-clamp-1 text-ellipse">
          {buttonText}
        </span>
        <Icons.Chevron.Down
          className={mergeStyles(
            'h-4 w-4 transition-transform',
            isOpen ? 'rotate-180' : ''
          )}
        />
      </button>

      {isOpen
        ? createPortal(
            <div
              ref={panelRef}
              id={panelId}
              role="listbox"
              aria-multiselectable="true"
              className="hide-scrollbar border border-grey-light bg-white rounded-2xl overflow-y-auto overflow-x-hidden px-2 pt-2 pb-2 fixed text-black"
              style={{
                top: position.top,
                left: position.left,
                width: position.width,
                maxHeight: 'min(320px, calc(100vh - 8rem))',
              }}
            >
              <div className="flex items-center justify-between px-2 pb-2">
                <span className="text-xs text-black">
                  {selectedValues.length} selected
                </span>
                <Button
                  buttonStyle="tertiary"
                  className="text-xs h-fit text-black border border-black hover:bg-black hover:text-white px-3 py-1 capitalize disabled:invisible"
                  disabled={selectedValues.length === 0}
                  onClick={() => onChange([])}
                >
                  Clear
                </Button>
              </div>
              <div className="mx-2 mb-1 h-px bg-grey-mid" />
              <div className="mx-1 mt-1 rounded-xl overflow-hidden">
                {cleanedOptions.map((option) => {
                  const optionValue = String(option.value);
                  const isChecked = selectedValues.includes(optionValue);

                  return (
                    <CheckboxRow
                      key={optionValue}
                      id={`${name}-${optionValue}`}
                      name={`${name}-${optionValue}`}
                      label={option.label}
                      checked={isChecked}
                      disabled={option.disabled}
                      onChange={() => toggleOption(optionValue)}
                      className={mergeStyles(
                        'gap-2 px-2 py-2 rounded-xl text-black',
                        `${
                          option.disabled
                            ? 'cursor-not-allowed text-grey-40'
                            : 'cursor-pointer hover:bg-orange hover:text-white'
                        }
                        ${isChecked ? 'font-bold' : ''}`
                      )}
                    />
                  );
                })}
                {cleanedOptions.length === 0 && (
                  <span>No filters available</span>
                )}
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}

export function ImageInput({
  name,
  label,
  inlineErrors = true,
  ...props
}: { name: string; label: string | ReactNode; inlineErrors?: boolean } & Record<
  string,
  any
>): ReactElement {
  const { formState, register } = useFormContext();
  const { previewUrl, ...passedProps } = props;

  return (
    <FieldWrapper>
      <Label
        label={
          <div className="flex gap-2 items-center">
            <label
              htmlFor={name}
              className={mergeStyles(
                props.className ?? '',
                'text-xs pl-2 font-bold'
              )}
            >
              {label}
            </label>
            {inlineErrors && formState.errors[name] ? (
              <ErrorMessage
                errors={formState.errors}
                name={name}
                render={({ message }) => <ErrorText message={message} />}
              />
            ) : null}
          </div>
        }
        fieldName={name}
      />
      <input
        {...passedProps}
        {...register(name, { ...formatValidationWithErrors(props, label) })}
        name={name}
        id={name}
        type="file"
        multiple={false}
        accept={ALLOWED_FILE_TYPES.join(',')}
        onChange={props.onChange}
      />
      {previewUrl && (
        <Image
          src={previewUrl}
          alt="Preview of the image you have selected"
          width={75}
          height={75}
        />
      )}
      {props.description && <Description description={props.description} />}
    </FieldWrapper>
  );
}

// The below are all used by the Input component
function DateInput({
  name,
  label,
  inlineErrors = true,
  ...props
}: { name: string; label: string | ReactNode; inlineErrors?: boolean } & Record<
  string,
  any
>): ReactElement {
  const { formState, register } = useFormContext();

  return (
    <FieldWrapper>
      <Label
        label={
          <div className="flex gap-2 items-center">
            <label
              htmlFor={name}
              className={mergeStyles(
                props.className ?? '',
                'text-xs pl-2 font-bold'
              )}
            >
              {label}
            </label>
            {inlineErrors && formState.errors[name] ? (
              <ErrorMessage
                errors={formState.errors}
                name={name}
                render={({ message }) => <ErrorText message={message} />}
              />
            ) : null}
          </div>
        }
        fieldName={name}
      />

      <input
        {...register(name, {
          ...formatValidationWithErrors(props, label),
          validate: props.validate ? props.validate : null,
        })}
        type="date"
        className="h-10 border border-solid border-grey-mid bg-white p-2.5 text-black placeholder:text-grey-dark data-[error=true]:border-red rounded-full"
      />
      {props.description && <Description description={props.description} />}
    </FieldWrapper>
  );
}

function NumberInput({
  name,
  label,
  inlineErrors = true,
  ...props
}: { name: string; label: string | ReactNode; inlineErrors?: boolean } & Record<
  string,
  any
>): ReactElement {
  const { formState, register } = useFormContext();

  return (
    <FieldWrapper>
      <Label
        label={
          <div className="flex gap-2 items-center">
            <label
              htmlFor={name}
              className={mergeStyles(
                props.className ?? '',
                'text-xs pl-2 font-bold'
              )}
            >
              {label}
            </label>
            {inlineErrors && formState.errors[name] ? (
              <ErrorMessage
                errors={formState.errors}
                name={name}
                render={({ message }) => <ErrorText message={message} />}
              />
            ) : null}
          </div>
        }
        fieldName={name}
      />
      <input
        {...props}
        className={mergeStyles(
          props.className ?? '',
          `h-10 border border-solid border-grey-mid bg-white 
        p-2.5 text-black placeholder:text-grey-dark rounded-full`
        )}
        {...register(name, {
          ...formatValidationWithErrors(props, label),
          validate: props.validate ? props.validate : null,
        })}
      />
      {props.description && <Description description={props.description} />}
    </FieldWrapper>
  );
}

function CheckboxInput({
  name,
  label,
  inlineErrors = true,
  ...props
}: { name: string; label: string | ReactNode; inlineErrors?: boolean } & Record<
  string,
  any
>): ReactElement {
  const { formState, register } = useFormContext();

  if (!props.options) {
    return (
      <div className="mb-4">
        <CheckboxRow
          id={name}
          value={name}
          label={label}
          className="mb-1 self-start gap-1"
          labelClassName="order-2 text-xs pl-2"
          inputClassName="order-1 w-fit"
          {...register(name, {
            ...formatValidationWithErrors(props, label),
            validate: props.validate ? props.validate : null,
          })}
          {...props}
        />
        {props.description && <Description description={props.description} />}
        {inlineErrors && formState.errors[name] ? (
          <ErrorMessage
            errors={formState.errors}
            name={name}
            render={({ message }) => <ErrorText message={message} />}
          />
        ) : null}
      </div>
    );
  }

  return (
    <FieldWrapper>
      <Label
        label={
          <div className="flex gap-2 items-center">
            <label
              htmlFor={name}
              className={mergeStyles(
                props.className ?? '',
                'text-xs pl-2 font-bold'
              )}
            >
              {label}
            </label>
            {inlineErrors && formState.errors[name] ? (
              <ErrorMessage
                errors={formState.errors}
                name={name}
                render={({ message }) => <ErrorText message={message} />}
              />
            ) : null}
          </div>
        }
        fieldName={name}
      />
      {props.options.map((o) => (
        <CheckboxRow
          key={o.value}
          id={o.value}
          value={o.value}
          label={o.label}
          labelClassName="checkbox"
          {...register(name, {
            ...formatValidationWithErrors(props, label),
            validate: props.validate ? props.validate : null,
          })}
        />
      ))}
    </FieldWrapper>
  );
}

function RadioInput({
  name,
  label,
  inlineErrors = true,
  ...props
}: { name: string; label: string | ReactNode; inlineErrors?: boolean } & Record<
  string,
  any
>): ReactElement {
  const { formState, register } = useFormContext();

  if (!props.options) {
    return <></>;
  }
  return (
    <FieldWrapper>
      <Label
        label={
          <div className="flex gap-2 items-center">
            <label
              htmlFor={name}
              className={mergeStyles(
                props.className ?? '',
                'text-xs pl-2 font-bold'
              )}
            >
              {label}
            </label>
            {inlineErrors && formState.errors[name] ? (
              <ErrorMessage
                errors={formState.errors}
                name={name}
                render={({ message }) => <ErrorText message={message} />}
              />
            ) : null}
          </div>
        }
        fieldName={name}
      />
      {props.options.map((o) => (
        <div key={o.value ?? o} className="flex items-center gap-2">
          <input
            {...register(name, {
              ...formatValidationWithErrors(props, label),
              validate: props.validate ? props.validate : null,
            })}
            {...props}
            id={o.value ?? o}
            name={name}
            value={o.value ? o.value : (o.label ?? o)}
            className="h-[1.5rem] border border-solid border-grey-mid bg-white 
            p-2.5 text-black placeholder:text-grey-dark"
          />
          <label htmlFor={o.value ?? o} className="radio">
            {o.label ?? o}
          </label>
        </div>
      ))}
    </FieldWrapper>
  );
}

function TextInput({
  name,
  label,
  inlineErrors = true,
  ...props
}: { name: string; label: string | ReactNode; inlineErrors?: boolean } & Record<
  string,
  any
>): ReactElement {
  const { formState, register } = useFormContext();

  return (
    <FieldWrapper>
      <Label
        label={
          <div className="flex gap-2 items-center">
            <label
              htmlFor={name}
              className={mergeStyles(
                props.className ?? '',
                'text-xs pl-2 font-bold'
              )}
            >
              {label}
            </label>
            {inlineErrors && formState.errors[name] ? (
              <ErrorMessage
                errors={formState.errors}
                name={name}
                render={({ message }) => <ErrorText message={message} />}
              />
            ) : null}
          </div>
        }
        fieldName={name}
      />
      <input
        {...props}
        className={mergeStyles(
          props.className ?? '',
          `h-10 min-w-[200px] border border-solid border-grey-mid 
        bg-white p-2.5 text-black placeholder:text-grey rounded-full`
        )}
        {...register(name, {
          ...formatValidationWithErrors(props, label),
          validate: props.validate ? props.validate : null,
        })}
      />
      {props.description && <Description description={props.description} />}
    </FieldWrapper>
  );
}

function SearchInput({
  name,
  label,
  inlineErrors = true,
  ...props
}: { name: string; label: string | ReactNode; inlineErrors?: boolean } & Record<
  string,
  any
>): ReactElement {
  const { formState, register, setValue, watch } = useFormContext();

  const queryValue = watch(name);

  return (
    <div className="flex flex-col gap-1">
      <VisuallyHidden>
        <Label label="Search term" fieldName={name} />
      </VisuallyHidden>

      <div
        className={`relative flex items-center border border-solid border-grey-mid bg-white text-grey-dark placeholder:text-grey-dark before:pointer-events-none before:absolute before:left-2 before:bottom-0 before:top-1 before:my-auto before:h-5 before:w-5 before:translate-y-0 before:bg-no-repeat before:bg-[url('data:image/svg+xml,%3Csvg%20stroke%3D%22hsl(0,0%25,40%25)%22%20fill%3D%22hsl(0,0%25,40%25)%22%20stroke-width%3D%220%22%20viewBox%3D%220%200%20512%20512%22%20height%3D%221em%22%20width%3D%221em%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M443.5%20420.2L336.7%20312.4c20.9-26.2%2033.5-59.4%2033.5-95.5%200-84.5-68.5-153-153.1-153S64%20132.5%2064%20217s68.5%20153%20153.1%20153c36.6%200%2070.1-12.8%2096.5-34.2l106.1%20107.1c3.2%203.4%207.6%205.1%2011.9%205.1%204.1%200%208.2-1.5%2011.3-4.5%206.6-6.3%206.8-16.7.6-23.3zm-226.4-83.1c-32.1%200-62.3-12.5-85-35.2-22.7-22.7-35.2-52.9-35.2-84.9%200-32.1%2012.5-62.3%2035.2-84.9%2022.7-22.7%2052.9-35.2%2085-35.2s62.3%2012.5%2085%2035.2c22.7%2022.7%2035.2%2052.9%2035.2%2084.9%200%2032.1-12.5%2062.3-35.2%2084.9-22.7%2022.7-52.9%2035.2-85%2035.2z%22%3E%3C%2Fpath%3E%3C%2Fsvg%3E')] rounded-full`}
      >
        <input
          {...props}
          className={mergeStyles(
            props.className,
            'h-10 p-2.5 text-black placeholder:italic pl-6 appearance-none [&::-webkit-search-cancel-button]:hidden rounded-full'
          )}
          placeholder={props.placeholder ?? 'What are you searching for?'}
          {...register(name, {
            ...formatValidationWithErrors(props, label),
            validate: props.validate ? props.validate : null,
          })}
        />
        <button
          type="button"
          onClick={() => setValue(name, '')}
          className={`absolute right-1 ${queryValue ? 'visible' : 'invisible'}`}
        >
          <Icons.Close size={20} aria-label="Clear search input" />
        </button>
      </div>
      {props.description && <Description description={props.description} />}
      {inlineErrors && formState.errors[name] ? (
        <ErrorMessage
          errors={formState.errors}
          name={name}
          render={({ message }) => <ErrorText message={message} />}
        />
      ) : null}
    </div>
  );
}

function EmailInput({
  name,
  label,
  inlineErrors = true,
  ...props
}: { name: string; label: string | ReactNode; inlineErrors?: boolean } & Record<
  string,
  any
>): ReactElement {
  const { formState, register } = useFormContext();

  const standardValidation = (value) => {
    if (value.length > 0 && !EMAIL_REGEX.test(value)) {
      const errorText = 'Invalid email address';
      return errorText;
    }
  };

  return (
    <FieldWrapper>
      <Label
        label={
          <div className="flex gap-2 items-center">
            <label
              htmlFor={name}
              className={mergeStyles(
                props.className ?? '',
                'text-xs pl-2 font-bold'
              )}
            >
              {label}
            </label>
            {inlineErrors && formState.errors[name] ? (
              <ErrorMessage
                errors={formState.errors}
                name={name}
                render={({ message }) => <ErrorText message={message} />}
              />
            ) : null}
          </div>
        }
        fieldName={name}
      />
      <input
        {...props}
        autoComplete="true"
        className={mergeStyles(
          props.className ?? '',
          `h-10 min-w-[200px] border border-solid border-grey-mid 
        bg-white p-2.5 text-black placeholder:text-grey-dark rounded-full`
        )}
        {...register(name, {
          ...formatValidationWithErrors(props, label),
          validate: props.validate ? props.validate : standardValidation,
        })}
      />
      {props.description && <Description description={props.description} />}
    </FieldWrapper>
  );
}

function PasswordInput({
  name,
  label,
  inlineErrors = true,
  validate,
  ...props
}: {
  name: string;
  label: string | ReactNode;
  inlineErrors?: boolean;
  validate?: (value: string) => string | undefined;
} & Record<string, any>): ReactElement {
  const { formState, register } = useFormContext();

  const [passwordVisibility, setPasswordVisibility] = useState(false);

  const passwordValidation = (value) => {
    if (!PASSWORD_REGEX.test(value)) {
      const errorText = validatePassword(value);
      return errorText;
    }
  };

  const inputProps = {
    ...props,
    type: passwordVisibility ? 'text' : 'password',
  };

  return (
    <FieldWrapper>
      <Label
        label={
          <div className="flex gap-2 items-center">
            <label
              htmlFor={name}
              className={mergeStyles(
                props.className ?? '',
                'text-xs pl-2 font-bold'
              )}
            >
              {label}
            </label>
            {inlineErrors && formState.errors[name] ? (
              <ErrorMessage
                errors={formState.errors}
                name={name}
                render={({ message }) => <ErrorText message={message} />}
              />
            ) : null}
          </div>
        }
        fieldName={name}
      />
      <div
        className={mergeStyles(
          props.className ?? '',
          'flex flex-row items-center'
        )}
      >
        <input
          {...inputProps}
          className="h-10 w-full border border-solid border-grey-mid bg-white p-2.5 text-black placeholder:text-grey-dark rounded-full"
          {...register(name, {
            ...formatValidationWithErrors(props, label),
            validate: validate ? validate : passwordValidation,
          })}
        />
        {!passwordVisibility ? (
          <Icons.Eye
            className="w-10 cursor-pointer text-grey-dark dark:text-grey-mid"
            size={24}
            onClick={() => setPasswordVisibility(true)}
          />
        ) : (
          <Icons.EyeOff
            className="w-10 cursor-pointer text-grey-dark dark:text-grey-mid"
            size={24}
            onClick={() => setPasswordVisibility(false)}
          />
        )}
      </div>
      {props.description && <Description description={props.description} />}
    </FieldWrapper>
  );
}
